import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { findRoots } from '../../../analytics/numerical-methods';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import { createEnumeratedCollection, memoizeByKey } from '../../../context/computed-collection';
import type { RootsParams } from './roots.model';

/**
 * Creates root points (x-intercepts) for any function type.
 * Uses numerical root-finding (bisection) to find where f(x) = 0.
 *
 * JSXGraph can find intersections between two objects (for roots we would create an invisible
 * x axis and find the intersection).
 *
 * However, JSXGraph's intersection requires a fixed index (0, 1, 2...) to specify which
 * intersection, it doesn't dynamically find all intersections.
 *
 * The currently custom implementation does the following:
 * - Dynamically finds all roots in the visible bounding box
 * - Adapts to viewport changes (roots outside view aren't computed)
 * - Handles any number of roots up to maxRoots
 */

export class RootsCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'roots';
  override readonly computedValueKeys = ['count', 'x0', 'x1', 'x2', 'x3', 'x4', 'x5', 'x6', 'x7', 'x8', 'x9'] as const;
  override readonly computedCollectionKeys = [{ name: '', properties: ['x', 'y'] }] as const;

  private readonly maxRoots = 10;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as RootsParams | undefined;
    if (!params?.of) {
      throw new Error('Roots feature requires params.of to specify the parent function');
    }
    // Use getEvaluableParent to support both functions and derivatives
    const parentFn = this.getEvaluableParent(params.of, context);
    const objects: GeometryElement[] = [];

    const style = resolveFeatureStyle('point', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    // Memoized root-finding
    const getRootPoints = memoizeByKey(
      () => {
        const bbox = board.getBoundingBox();
        const roots = findRoots(parentFn.evaluate, bbox[0], bbox[2], { maxResults: this.maxRoots });
        return roots.map((x) => ({ x, y: 0 }));
      },
      () => {
        const bbox = board.getBoundingBox();
        const fnParams =
          'getParams' in parentFn ? (parentFn as { getParams: () => Record<string, number> }).getParams() : {};
        return `${bbox[0].toFixed(2)},${bbox[2].toFixed(2)}|${Object.values(fnParams)
          .map((v) => v.toFixed(4))
          .join(',')}`;
      },
    );

    for (let i = 0; i < this.maxRoots; i++) {
      const idx = i;
      const result = elementFactory.point({
        coords: [
          () => {
            const roots = getRootPoints();
            return idx < roots.length ? roots[idx].x : NaN;
          },
          0,
        ],
        name: 'root',
        style: { ...style, visible },
      });
      objects.push(result.point);
      if (result.label) {
        objects.push(result.label);
      }
    }

    // Legacy flat keys (x0..x9) preserved as aliases
    const computedValues: Record<string, () => number> = {
      count: () => getRootPoints().length,
    };
    for (let j = 0; j < this.maxRoots; j++) {
      const idx = j;
      computedValues[`x${idx}`] = () => {
        const roots = getRootPoints();
        return idx < roots.length ? roots[idx].x : NaN;
      };
    }

    // Collection for indexed access (e.g. f-roots.0.x)
    const collection = createEnumeratedCollection(getRootPoints, this.maxRoots);

    return {
      id,
      type: 'roots',
      objects,
      getComputedValues: () => computedValues,
      getComputedCollections: () => ({ '': collection }),
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
